function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/** @jsxRuntime classic */

/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import React from 'react';
import Inputmask from 'inputmask';
import classnames from 'classnames';
import { migratoryWebCitizen as colors } from '../../../../../Colors';
import { useDeepCompare } from '../../../../../../hooks/use-deep-compare-effect';
import useOnDidUpdate from '../../../../../../hooks/use-on-didUpdate';
import { mediaQuery, getCursorPosition } from '../../../../../../utils';
export function FormDiv({
  children,
  containerStyle
}) {
  return jsx("div", {
    style: {
      marginBottom: 23,
      ...containerStyle
    }
  }, children);
}
/**
 * This component represent a text area.
 * @param {*} children What will be shown below the text area.
 * @param {number} width Width of the container of the text area.
 * @param {JSON} style Custom text area styles.
 * @param {boolean} capitalLetter Determine if capitalize the text or not.
 * @param {*} rest All other supported props.
 */

export function TextArea({
  children = null,
  width = 524,
  style = {},
  capitalLetter = false,
  ...rest
}) {
  const [count, setCount] = React.useState(0);
  const textAreaRef = React.useRef(null);
  const {
    showCount = false,
    mask = {},
    rows,
    error,
    touched,
    onChange
  } = rest; // eslint-disable-next-line no-param-reassign

  delete rest.onChange; // eslint-disable-next-line no-param-reassign

  delete rest.showCount; // eslint-disable-next-line no-param-reassign

  delete rest.touched;

  const countWords = ({
    target: {
      value
    }
  }) => {
    setCount(value.length);
  };

  React.useEffect(() => {
    if (mask) {
      const maskProps = {
        autoUnmask: true,
        showMaskOnHover: false,
        ...mask
      }; // this lib cause re-render

      const inputmask = new Inputmask(maskProps);
      inputmask.mask(textAreaRef.current);
    }
  }, useDeepCompare([mask]));

  const renderCount = () => {
    const {
      maxLength
    } = rest;
    return jsx("span", {
      style: {
        position: 'absolute',
        bottom: 5,
        right: 5,
        fontSize: 'calc(10px + (12 - 10) * ((100vw - 768px) / (1600 - 768)))',
        color: colors.pacificBlue
      }
    }, count, maxLength ? `/${maxLength}` : '');
  };

  const onTextAreaChange = e => {
    const {
      value
    } = e.target;

    if (showCount) {
      countWords(e);
    }

    if (onChange) {
      if (onChange.name.length > 0) {
        onChange(capitalLetter ? value.toUpperCase() : value);
        return;
      } // eslint-disable-next-line no-param-reassign


      e.target.value = capitalLetter ? value.toUpperCase() : value;
      onChange(e);
    }
  };

  const cssStyle = {
    backgroundColor: colors.white,
    border: `1px solid ${colors.valhalla}`,
    boxShadow: '0px 7px 64px rgba(0, 0, 0, 0.07)',
    borderRadius: 6,
    width,
    height: rows ? '' : 50,
    padding: '14px 16px',
    fontSize: 15,
    color: colors.valhalla,
    boxSizing: 'border-box',
    resize: 'none',
    fontFamily: 'Rubik',
    ...style
  };
  return jsx("div", {
    css: inputCss,
    style: {
      display: 'flex',
      flexDirection: 'column'
    }
  }, jsx("div", {
    style: {
      position: 'relative',
      width
    }
  }, jsx("textarea", _extends({
    ref: textAreaRef,
    onChange: onTextAreaChange,
    type: "text",
    className: error && error.length > 0 && touched ? 'error' : '',
    style: cssStyle
  }, rest)), children || '', showCount ? renderCount() : ''));
}
/**
 * This component represent label used in forms.
 * @param {string} children Label text.
 * @param {string} text Label text.
 * @param {JSON} containerStyle Custom Label styles.
 * @param {*} props All other supported props.
 */

export const FormLabel = /*#__PURE__*/React.memo(({
  children,
  text,
  containerStyle,
  ...props
}) => {
  return jsx("div", {
    className: "formLabel",
    style: {
      marginBottom: 10
    }
  }, jsx("label", _extends({
    style: {
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: 12,
      color: colors['dark-gray'],
      marginLeft: 15,
      ...containerStyle
    }
  }, props), children || text));
}, (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps));
/**
 * This component represent a div with row styles.
 * @param {*} children Div content.
 * @param {JSON} containerStyle Custom div styles.
 * @param {*} props All other supported props.
 */

export function DivRow({
  children,
  containerStyle,
  ...props
}) {
  return jsx("div", _extends({
    style: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: 23,
      ...containerStyle
    }
  }, props), children);
}
/**
 * This component represent a div with column styles.
 * @param {*} children Div content.
 * @param {JSON} containerStyle Custom div styles.
 * @param {*} props All other supported props.
 */

export function DivColumn({
  children,
  containerStyle,
  ...props
}) {
  return jsx("div", _extends({
    style: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      ...containerStyle
    }
  }, props), children);
}
/**
 * This component represent a title.
 * @param {*} children Title content.
 * @param {*} props All other supported props.
 */

export const Title = /*#__PURE__*/React.memo(({
  children,
  containerStyle
}) => {
  return jsx("h1", {
    style: {
      fontSize: 26,
      color: colors.cerulean,
      textTransform: 'uppercase',
      ...containerStyle
    }
  }, children);
}, (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps));
/**
 * This component represent a check box used in forms.
 * @param {*} children Text in the check box.
 * @param {string} text Text in the check box.
 * @param {string} color Color for check box.
 * @param {JSON} containerStyle Custom styles apply to the div that wraps the checkbox.
 * @param {string} className ClassName used to apply predetermine styles to the div that wraps the checkbox.
 * @param {boolean} disabled Boolean to know if checked or not.
 * @param {*} rest All other supported props.
 */

export const CheckBox = /*#__PURE__*/React.memo(({
  children,
  text,
  color = 'fireEngineRed',
  containerStyle,
  className,
  disabled = false,
  spanStyle,
  ...rest
}) => {
  return jsx("div", {
    css: [checkBox(color), {
      display: 'flex',
      flexDirection: 'row',
      ...containerStyle
    }]
  }, jsx("div", {
    className: classnames('checkbox-container', className)
  }, jsx("input", _extends({
    type: "checkbox"
  }, rest, {
    disabled: disabled
  })), jsx("span", {
    className: "checkmark"
  })), jsx("div", null, jsx("label", {
    css: spanStyle ? { ...spanStyle
    } : {}
  }, children || text)));
});

const checkBox = color => css`
  label {
    color: #333333;
    font-size: 12px;
  }

  .checkbox-container {
    position: relative;
    cursor: pointer;
    width: 24px;
    height: 24px;
    margin-right: 11px;
  }

  .checkbox-container input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 100%;
    width: 100%;
    margin: 0;
    z-index: 10;
  }

  .checkmark {
    position: absolute;
    top: 0;
    left: 0;
    height: 18px;
    width: 18px;
    border: 2px solid ${colors[color]};
    border-radius: 4px;
  }

  .checkbox-container input:checked ~ .checkmark {
    background-color: ${colors[color]};
    border: 1px solid ${colors[color]};
    height: 20px;
    width: 20px;
  }

  .checkbox-container input:disabled ~ .checkmark {
    background-color: #dadada;
    border: 1px solid #dadada;
    height: 20px;
    width: 20px;
  }

  .checkmark:after {
    content: '';
    position: absolute;
    display: none;
  }

  .checkbox-container input:checked ~ .checkmark:after {
    display: block;
  }

  .checkbox-container .checkmark:after {
    left: 6px;
    top: 3px;
    width: 6px;
    height: 10px;
    border: 1px solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
  .checkbox-container input:disabled ~ .checkmark:after {
    left: 6px;
    top: 3px;
    width: 6px;
    height: 10px;
    border: 1px solid #214a61;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  input[readonly],
  input:disabled {
    cursor: not-allowed;
  }
`;
/**
 * This component represent an input text field in forms.
 * @param {*} children What will be shown below the input.
 * @param {number} width Width of the container of the input.
 * @param {JSON} style Custom input styles.
 * @param {boolean} capitalLetter Determine if capitalize the text or not.
 * @param {*} rest All other supported props.
 */


export const InputText = ({
  children = null,
  width = 524,
  style = {},
  capitalLetter = false,
  ...rest
}) => {
  const onDidUpdate = useOnDidUpdate();
  const [count, setCount] = React.useState(0);
  const inputRef = React.useRef(null);
  const {
    showCount = false,
    mask = null,
    error,
    touched,
    onChange
  } = rest; // eslint-disable-next-line no-param-reassign

  delete rest.onChange; // eslint-disable-next-line no-param-reassign

  delete rest.showCount; // eslint-disable-next-line no-param-reassign

  delete rest.touched;

  const countWords = ({
    target: {
      value
    }
  }) => {
    setCount(value.length);
  };

  React.useEffect(() => {
    if (mask) {
      const maskProps = {
        autoUnmask: true,
        showMaskOnHover: false,
        ...mask
      }; // this lib cause re-render

      const inputmask = new Inputmask(maskProps);
      inputmask.mask(inputRef.current);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, useDeepCompare([mask]));
  React.useEffect(() => {
    if (showCount) {
      const {
        value
      } = rest;

      if (value.length === 0 && count > 0) {
        setCount(0);
      }

      if (value.length > 0 && count === 0) {
        setCount(value.length);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [rest.value]);

  const renderCount = () => {
    const {
      maxLength
    } = rest;
    return jsx("span", {
      className: "character-counter"
    }, count, maxLength ? `/${maxLength}` : '');
  };

  function onDidUpdateExec(event, text) {
    const input = event.target;
    const cursor = input.selectionStart;
    const difference = event.target.value.length - text.length;
    const newCursor = getCursorPosition(text, cursor - difference);
    onDidUpdate(() => {
      input.selectionStart = newCursor;
      input.selectionEnd = newCursor;
    });
  }

  const onInputChange = e => {
    const {
      value
    } = e.target;

    if (showCount) {
      countWords(e);
    }

    if (onChange) {
      if (onChange.name.length > 0) {
        const text = onChange(capitalLetter ? value.toUpperCase() : value);

        if (text && !mask) {
          onDidUpdateExec(e, text);
        }

        return;
      } // eslint-disable-next-line no-param-reassign


      e.target.value = capitalLetter ? value.toUpperCase() : value;
      const text = onChange(e);

      if (text) {
        onDidUpdateExec(e, text);
      }
    }
  };

  const cssStyle = {
    backgroundColor: colors.white,
    border: `1px solid ${colors.valhalla}`,
    boxShadow: '0px 7px 64px rgba(0, 0, 0, 0.07)',
    borderRadius: 6,
    width,
    height: 50,
    padding: '14px 16px',
    fontSize: 15,
    color: colors.valhalla,
    boxSizing: 'border-box',
    ...style
  };
  return jsx("div", {
    css: inputCss,
    style: {
      display: 'flex',
      flexDirection: 'column'
    }
  }, jsx("div", {
    css: {
      position: 'relative',
      width,
      [mediaQuery[0]]: {
        width: '100% !important'
      }
    }
  }, jsx("input", _extends({
    ref: inputRef,
    onChange: onInputChange,
    type: "text",
    className: error && error.length > 0 && touched ? 'error' : '',
    style: cssStyle
  }, rest)), children || '', showCount ? renderCount() : ''));
};
const inputCss = css`
  .error {
    border: 1px solid ${colors.fireEngineRed} !important;
  }

  input:focus,
  input:hover,
  textarea:hover,
  textarea:focus {
    border: 1px solid ${colors.pacificBlue} !important;
    color: ${colors.pacificBlue} !important;
    box-shadow: 0 2px 3px 0 rgba(34, 36, 38, 0.15) !important;
  }

  input[readonly],
  input:disabled,
  textarea[readonly],
  textarea:disabled {
    cursor: not-allowed;
    border: none !important;
    color: #214a61 !important;
    background: #a0b9c8 !important;
    -webkit-text-fill-color: #214a61;
  }

  input[readonly]::placeholder,
  input:disabled::placeholder,
  textarea[readonly]::placeholder,
  textarea:disabled::placeholder {
    opacity: 1;
  }

  input[readonly]::-webkit-input-placeholder,
  input:disabled::-webkit-input-placeholder,
  textarea[readonly]::-webkit-input-placeholder,
  textarea:disabled::-webkit-input-placeholder {
    opacity: 1;
  }

  input[readonly]::-moz-input-placeholder,
  input:disabled::-moz-input-placeholder,
  textarea[readonly]::-moz-input-placeholder,
  textarea:disabled::-moz-input-placeholder {
    opacity: 1;
  }

  .character-counter {
    position: absolute;
    bottom: 5px;
    right: 5px;
    font-size: calc(10px + (12 - 10) * ((100vw - 768px) / (1600 - 768)));
    color: ${colors.pacificBlue};
  }

  input[readonly] + span,
  input:disabled + span,
  textarea[readonly] + span,
  textarea:disabled + span {
    color: #214a61;
  }
`;